import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { retry } from 'rxjs/operators';
import { environment } from './../../environments/environment';
import { AlertService } from './alert.service';
import { ConfigService } from './config.service';
import { BookService } from './book.service';
import { ActivatedRoute } from '@angular/router';
import { Book } from '../data/book';

@Injectable({
  providedIn: 'root'
})

export class ImagesCacheService {

  images: {
    src: string,
    img: SafeResourceUrl
  }[] = [];

  dcmImages: {
    src: string,
    imageId: string
  }[] = [];

  urlCreator = window.URL || window.webkitURL
  imagesQueue: { studyUID: string, route: string }[] = [];
  gettingFile = false;

  cinemaCounter = 0;

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private configService: ConfigService,
    private bookService: BookService,
    private alertService: AlertService,
  ) {
    this.configService.getConfig().then(c => {
      const interval = (c.simple_viewer_cinema_interval != null) ? c.simple_viewer_cinema_interval : 100;
      setInterval(() => {
        this.cinemaCounter++;
        if (this.cinemaCounter == Number.MAX_SAFE_INTEGER - 1) this.cinemaCounter = 0;
      }, interval);
    })
  }

  getFile(studyUID: string, route: string): Promise<Blob> {
    return new Promise<Blob>(async (resolve, reject) => {
      if (!environment.demo) {
        const url = await this.configService.BOOK_URL + '/bookAccess/getFile?studyUID=' + studyUID;
        this.http.post(url, route, { withCredentials: true, responseType: 'blob' }).pipe(retry(3))
          .subscribe(
            next => {
              if (next == null) {
                console.warn("no se ha encontrado la imagen " + route + ". Se va a poner solid_black")
                this.http.get('assets/img/Solid_black.png', { responseType: 'blob' }).subscribe(res => resolve(res));
              }
              resolve(next);
            },
            error => {
              reject(error);
            });
      } else {
        this.http.get(route, { responseType: 'blob' }).subscribe(
          next => {
            resolve(next);
          },
          error => {
            reject(error);
          });
      }
    })
  }

  isLoaded(route: string): boolean {
    if (
      this.images.findIndex(img => img.src == route) != -1
      || this.dcmImages.findIndex(img => img.src == route) != -1
      || this.imagesQueue.findIndex(img => img.route == route) != -1
    ) {
      return true;
    }
    return false;
  }

  areLoaded(route: string[]): boolean {
    return route.map(r => this.isLoaded(r)).every(b => b == true);
  }

  getDcmImageId(studyUID: string, route: string): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      let image = this.dcmImages.find(img => img.src == route);
      if (image != null) {
        resolve(image.imageId);
      } else {
        this.addImage(studyUID, route);
        const int = setInterval(() => {
          const dcm = this.dcmImages.find(d => d.src == route);
          if (dcm != null) {
            clearInterval(int);
            resolve(dcm.imageId);
          }
        }, 50)
      }
    })
  }

  getFilesRecursive() {
    this.gettingFile = true;
    if (this.imagesQueue.length == 0) {
      this.gettingFile = false;
      return;
    } else {
      const imageToGet = this.imagesQueue.shift();
      this.getFile(imageToGet!.studyUID, imageToGet!.route).then(img => {
        if (
          imageToGet!.route.endsWith('.png') ||
          imageToGet!.route.endsWith('.PNG') ||
          imageToGet!.route.endsWith('.mp4') ||
          imageToGet!.route.endsWith('.MP4')
        ) {
          const image = {
            src: imageToGet!.route,
            img: this.sanitizer.bypassSecurityTrustResourceUrl(this.urlCreator.createObjectURL(img))
          }
          console.log("pedida imagen:", image)
          this.images.push(image);
        } else {
          const fileURL = this.urlCreator.createObjectURL(img);
          const imageId = 'wadouri:' + fileURL;
          this.dcmImages.push({ imageId, src: imageToGet!.route });
        }
        this.getFilesRecursive();
      }).catch(err => {
        console.error('ERROR: No se ha podido cargar una imagen');
        this.getFilesRecursive();
        if (
          imageToGet!.route.endsWith('.png') ||
          imageToGet!.route.endsWith('.PNG') ||
          imageToGet!.route.endsWith('.mp4') ||
          imageToGet!.route.endsWith('.MP4')
        ) {
          const image = {
            src: imageToGet!.route,
            img: 'assets/img/Solid_black.png'
          }
          this.images.push(image);
        } else {
          this.alertService.alert('Ha ocurrido un error cargando la imagen. Por favor, compruebe la conexión a internet y vuelva a probar')
        }
      })
    }
  }

  addImage(studyUID: string, route: string) {
    if (route == null || route == '') return;
    if (!this.isLoaded(route)) {
      this.imagesQueue.push({ studyUID, route });
      if (!this.gettingFile) {
        this.getFilesRecursive();
      }
    }
  }

  getImage(route: string[]) {
    let index = 0;
    if (this.areLoaded(route)) {
      index = this.cinemaCounter % route.length;
    }
    const img = this.images.find(img => img.src == route[index]);
    if (img != null) {
      return img.img;
    }
    return undefined;
  }

  getSingleImage(route: string) {
    if (this.isLoaded(route)) {
      const img = this.images.find(img => img.src == route);
      if (img != null) {
        return img.img;
      }
    }
    return undefined;
  }

  getRouteByImageId(imageId: string) {
    return this.dcmImages.find(img => img.imageId == imageId)?.src;
  }

  buildURIs(studyUID: string, instanceUID: string, token:string) {
    return new Promise<{ dcm: string | null, png: string | null }>((resolve, reject) => {
      if (environment.demo) {
        const route = `assets/books/${studyUID}/dcms/${instanceUID}.dcm`
        resolve({ dcm: 'wadouri:' + route, png: null })
      } else {
        this.configService.BOOK_URL.then(url => {
          this.configService.getConfig().then(c => {
            resolve(this.buildURIsSync(studyUID, instanceUID, url, c.thumbnails_size, token));
          })
        }).catch(err => {
          console.error(err);
          reject(err);
        })
      }
    })
  }

  buildAllURIs(studyUID: string, instanceUIDs: string[], token: string) {
    return new Promise<{ dcm: string | null, png: string | null }[]>((resolve, reject) => {
      if (environment.demo) {
        let uris: {
          dcm: string | null;
          png: string | null;
        }[] = []
        instanceUIDs.forEach(instanceUID => {
          const route = `assets/books/${studyUID}/dcms/${instanceUID}.dcm`
          uris.push({ dcm: 'wadouri:' + route, png: null })

        })
        resolve(uris);
      } else {
        this.configService.BOOK_URL.then(url => {
          let uris: {
            dcm: string | null;
            png: string | null;
          }[] = []
          instanceUIDs.forEach(instanceUID => {
            this.configService.getConfig().then(c => {
              uris.push(this.buildURIsSync(studyUID, instanceUID, url, c.thumbnails_size, token));
            })
          })
          resolve(uris);
        }).catch(err => {
          console.error(err);
          reject(err);
        })
      }
    })
  }

  private buildURIsSync(studyUID: string, instanceUID: string, url: string, size: number, token: string) {
    const dcmURI = `wadouri:${url}/image/getImage?studyUID=${studyUID}&seriesUID=UNKNOWN&objectUID=${instanceUID}&requestType=WADO&tkn=${token}`;
    const PngURI = `${url}/image/getImage?studyUID=${studyUID}&seriesUID=UNKNOWN&objectUID=${instanceUID}&requestType=WADO&contentType=image/jpeg&rows=${size}&columns=${size}&tkn=${token}`;
    return ({ dcm: dcmURI, png: PngURI });
  }

}
